import api from "@/lib/api";

/**
 * Settings API helpers
 *
 * Detection thresholds valid field names and bounds (for autocomplete):
 * - speed: number (20 - 200) // km/h
 * - temperature: number (80 - 160) // °C (vehicle engine / ambient)
 * - fire_temperature: number (120 - 250) // °C (fire-specific sensor)
 * - passenger_limit: number (1 - 100) // passengers
 * - dedup_seconds: number (10 - 3600) // seconds (dedup window)
 * - consecutive_readings: number (1 - 10) // number of consecutive readings required
 */

/**
 * Get detection thresholds
 * @returns {Promise<any>} response.data
 */
export async function getDetectionThresholds() {
  const response = await api.get(`/settings/detection`);
  return response.data;
}

/**
 * Update detection thresholds with a partial payload.
 * Only include fields you want to change, e.g. { speed: 90, dedup_seconds: 60 }
 * Valid keys: speed, temperature, fire_temperature, passenger_limit, dedup_seconds, consecutive_readings
 * @param {Object} partialPayload
 * @returns {Promise<any>} response.data
 */
export async function updateDetectionThresholds(partialPayload) {
  const response = await api.patch(`/settings/detection`, partialPayload);
  return response.data;
}

/**
 * List platform policies
 * @returns {Promise<any>} response.data
 */
export async function listPolicies() {
  const response = await api.get(`/settings/policies`);
  return response.data;
}

/**
 * Get a single policy by slug
 * @param {string} slug
 * @returns {Promise<any>} response.data
 */
export async function getPolicy(slug) {
  const response = await api.get(`/settings/policies/${encodeURIComponent(slug)}`);
  return response.data;
}

/**
 * Create a new policy
 * @param {{slug:string, title:string, status:string, body:string}} payload
 * @returns {Promise<any>} response.data
 */
export async function createPolicy({ slug, title, status, body }) {
  const response = await api.post(`/settings/policies`, { slug, title, status, body });
  return response.data;
}

/**
 * Update an existing policy
 * @param {string} slug
 * @param {{title?:string, status?:string, body?:string}} payload
 * @returns {Promise<any>} response.data
 */
export async function updatePolicy(slug, payload) {
  const response = await api.patch(`/settings/policies/${encodeURIComponent(slug)}`, payload);
  return response.data;
}

/**
 * Upload a document for a policy slug.
 * The file will be sent as multipart/form-data under field name `file`.
 * Let axios set the Content-Type boundary automatically — do not set it manually.
 * @param {string} slug
 * @param {File|Blob} file
 * @returns {Promise<any>} response.data
 */
export async function uploadPolicyDocument(slug, file) {
  const form = new FormData();
  form.append("file", file);

  const response = await api.post(`/settings/policies/${encodeURIComponent(slug)}/document`, form);
  return response.data;
}

/**
 * Get a policy document URL (presigned or proxied)
 * Returns an object like { url, filename, expires_in }
 * @param {string} slug
 * @returns {Promise<any>} response.data
 */
export async function getPolicyDocumentUrl(slug) {
  const response = await api.get(`/settings/policies/${encodeURIComponent(slug)}/document`);
  return response.data;
}

export default {
  getDetectionThresholds,
  updateDetectionThresholds,
  listPolicies,
  getPolicy,
  createPolicy,
  updatePolicy,
  uploadPolicyDocument,
  getPolicyDocumentUrl,
};
